import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

// Country data with phone codes and formatting patterns
const COUNTRIES = [
  { code: 'US', name: 'United States', 
    dialCode: '+1', flag: '🇺🇸', format: '(###) ###-####', minLength: 10, maxLength: 10 },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦', format: '(###) ###-####', minLength: 10, maxLength: 10 },
  { code: 'GB', name: 'United Kingdom', 
    dialCode: '+44', flag: '🇬🇧', format: '#### ### ####', minLength: 10, maxLength: 11 },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺', format: '#### ### ###', minLength: 9, maxLength: 9 },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷', format: '## ## ## ## ##', minLength: 10, maxLength: 10 },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪', format: '### ### ####', minLength: 10, maxLength: 12 },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹', format: '### ### ####', minLength: 10, maxLength: 11 },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸', format: '### ### ###', minLength: 9, maxLength: 9 },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', flag: '🇳🇱', format: '## ### ####', minLength: 9, maxLength: 9 },
  { code: 'BE', name: 'Belgium', dialCode: '+32', flag: '🇧🇪', format: '### ## ## ##', minLength: 9, maxLength: 9 },
  { code: 'CH', name: 'Switzerland', dialCode: '+41', flag: '🇨🇭', format: '## ### ## ##', minLength: 9, maxLength: 9 },
  { code: 'AT', name: 'Austria', dialCode: '+43', flag: '🇦🇹', format: '### ### ####', minLength: 10, maxLength: 13 },
  { code: 'SE', name: 'Sweden', dialCode: '+46', flag: '🇸🇪', format: '## ### ## ##', minLength: 9, maxLength: 9 },
  { code: 'NO', name: 'Norway', dialCode: '+47', flag: '🇳🇴', format: '### ## ###', minLength: 8, maxLength: 8 },
  { code: 'DK', name: 'Denmark', dialCode: '+45', flag: '🇩🇰', format: '## ## ## ##', minLength: 8, maxLength: 8 },
  { code: 'FI', name: 'Finland', dialCode: '+358', flag: '🇫🇮', format: '## ### ####', minLength: 9, maxLength: 10 },
  { code: 'IE', name: 'Ireland', dialCode: '+353', flag: '🇮🇪', format: '## ### ####', minLength: 9, maxLength: 9 },
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹', format: '### ### ###', minLength: 9, maxLength: 9 },
  { code: 'GR', name: 'Greece', dialCode: '+30', flag: '🇬🇷', format: '### ### ####', minLength: 10, maxLength: 10 },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵', format: '## #### ####', minLength: 10, maxLength: 11 },
  { code: 'KR', name: 'South Korea', 
    dialCode: '+82', flag: '🇰🇷', format: '## #### ####', minLength: 10, maxLength: 11 },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳', format: '### #### ####', minLength: 11, maxLength: 11 },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳', format: '##### #####', minLength: 10, maxLength: 10 },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬', format: '#### ####', minLength: 8, maxLength: 8 },
  { code: 'HK', name: 'Hong Kong', dialCode: '+852', flag: '🇭🇰', format: '#### ####', minLength: 8, maxLength: 8 },
  { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷', format: '## ##### ####', minLength: 10, maxLength: 11 },
  { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽', format: '## #### ####', minLength: 10, maxLength: 10 },
  { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷', format: '## #### ####', minLength: 10, maxLength: 11 },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦', format: '## ### ####', minLength: 9, maxLength: 9 },
  { code: 'IL', name: 'Israel', dialCode: '+972', flag: '🇮🇱', format: '## ### ####', minLength: 9, maxLength: 9 },
  { code: 'AE', name: 'United Arab Emirates', 
    dialCode: '+971', flag: '🇦🇪', format: '## ### ####', minLength: 9, maxLength: 9 },
  { code: 'TR', name: 'Turkey', dialCode: '+90', flag: '🇹🇷', format: '### ### ####', minLength: 10, maxLength: 10 },
  { code: 'RU', name: 'Russia', dialCode: '+7', flag: '🇷🇺', format: '### ### ## ##', minLength: 10, maxLength: 10 }
];

const PhoneNumberField = ({
  label = 'Phone Number',
  value = '',
  onChange,
  onBlur,
  onCountryChange,
  defaultCountry = 'US',
  placeholder = 'Enter phone number',
  required = false,
  disabled = false,
  error = '',
  requiredMessage = 'Phone number is required',
  invalidFormatMessage = 'Invalid phone number format',
  validateOnBlur = true,
  validateFormat = true,
  className = '',
  containerClassName = ''
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [selectedCountry, setSelectedCountry] = useState(
    COUNTRIES.find(c => c.code === defaultCountry) || COUNTRIES[0]
  );
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Validation state
  const [validationError, setValidationError] = useState('');
  const [hasBlurred, setHasBlurred] = useState(false);

  // Determine which error to show (external error takes precedence)
  const displayError = error || (hasBlurred && validationError);

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!searchTerm) return COUNTRIES;
    return COUNTRIES.filter(country => 
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.dialCode.includes(searchTerm)
    );
  }, [searchTerm]);

  // Format phone number according to country pattern
  const formatPhoneNumber = (number, pattern) => {
    const cleaned = number.replace(/\D/g, '');
    let formatted = '';
    let numberIndex = 0;
    
    for (let i = 0; i < pattern.length && numberIndex < cleaned.length; i++) {
      if (pattern[i] === '#') {
        formatted += cleaned[numberIndex];
        numberIndex++;
      } else {
        formatted += pattern[i];
      }
    }
    
    return formatted;
  };

  // Convert to E.164 format
  const toE164Format = (countryCode, localNumber) => {
    const cleaned = localNumber.replace(/\D/g, '');
    if (!cleaned) return '';
    return `${countryCode}${cleaned}`;
  };

  // Validate phone number format for the selected country
  const validatePhoneFormat = (phoneValue, country) => {
    if (!validateFormat) return true;
    
    const cleaned = phoneValue.replace(/\D/g, '');
    
    // Check if the length is within the valid range for this country
    if (cleaned.length < country.minLength || cleaned.length > country.maxLength) {
      setValidationError(invalidFormatMessage);
      return false;
    }
    
    setValidationError('');
    return true;
  };

  // Validate required field
  const validateRequired = (phoneValue) => {
    if (required && validateOnBlur) {
      const cleanedPhone = phoneValue.replace(/\D/g, '');
      if (!cleanedPhone) {
        setValidationError(requiredMessage);
        return false;
      } else {
        return true;
      }
    }
    return true;
  };

  // Combined validation function
  const validatePhone = (phoneValue, country) => {
    // First check if required field is filled
    if (!validateRequired(phoneValue)) {
      return false;
    }
    
    // Only validate format if the field has content
    const cleanedPhone = phoneValue.replace(/\D/g, '');
    if (cleanedPhone && validateFormat) {
      return validatePhoneFormat(cleanedPhone, country);
    }
    
    return true;
  };

  // Parse existing E.164 number
  useEffect(() => {
    if (value) {
      const newValue = value.startsWith('+') ? value : `+1 ${value}`;
      // Find matching country by dial code
      const country = COUNTRIES.find(c => newValue.startsWith(c.dialCode));
      if (country) {
        setSelectedCountry(country);
        const localNumber = newValue.substring(country.dialCode.length);
        setPhoneNumber(formatPhoneNumber(localNumber, country.format));
      }
    }
  }, [value]);

  // Clear validation error when external error is provided
  useEffect(() => {
    if (error) {
      setValidationError('');
    }
  }, [error]);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setSearchTerm('');
    onCountryChange?.(country);
    
    // Reformat the current phone number for the new country
    const cleanedNumber = phoneNumber.replace(/\D/g, '');
    const reformatted = formatPhoneNumber(cleanedNumber, country.format);
    setPhoneNumber(reformatted);
    
    // Update the formatted output
    const e164 = toE164Format(country.dialCode, cleanedNumber);
    onChange?.(e164);
    
    // Clear any format validation errors when changing country
    if (hasBlurred && validationError === invalidFormatMessage) {
      setValidationError('');
    }
  };

  const handlePhoneNumberChange = (e) => {
    const input = e.target.value;
    const formatted = formatPhoneNumber(input, selectedCountry.format);
    setPhoneNumber(formatted);
    
    // Clear validation error when user starts typing after blur
    if (hasBlurred && validationError && input.trim()) {
      setValidationError('');
    }
    
    // Convert to E.164 and call onChange
    const e164 = toE164Format(selectedCountry.dialCode, input);
    onChange?.(e164);
  };

  const handleBlur = (e) => {
    setHasBlurred(true);
    
    // Validate the phone number format before calling onBlur
    const currentLocalNumber = phoneNumber.replace(/\D/g, '');
    const isValid = currentLocalNumber?.length > 0 &&
       validatePhone(currentLocalNumber, selectedCountry);
    if (!isValid) {
      // If invalid, prevent blur event from propagating
      setValidationError('Invalid phone number');
      e.preventDefault();
      return;
    }
    // Call external onBlur handler regardless of validation result
    // This maintains backward compatibility
    if (onBlur) {
      onBlur(e);
    }
  };

  // Theme-based styling
  const labelClassName = clsx(
    'block text-sm font-medium mb-2',
    {
      'text-slate-200': isDark,
      'text-gray-700': !isDark,
      'text-red-400': displayError && isDark,
      'text-red-600': displayError && !isDark
    }
  );

  const containerClasses = clsx(
    'relative h-[42px] flex rounded-lg border transition-colors',
    'focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent',
    {
      'bg-slate-800 border-slate-600': isDark && !displayError,
      'bg-white border-gray-300': !isDark && !displayError,
      'border-red-500 bg-red-50': displayError && !isDark,
      'border-red-400 bg-slate-800': displayError && isDark,
      'opacity-50 cursor-not-allowed': disabled
    },
    className
  );

  const countryButtonClassName = clsx(
    'flex items-center gap-2 px-3 py-1 border-r transition-colors',
    'focus:outline-none focus:ring-0',
    {
      'border-slate-600 text-slate-200 hover:bg-slate-700': isDark && !displayError,
      'border-gray-300 text-gray-700 hover:bg-gray-50': !isDark && !displayError,
      'border-red-500': displayError && !isDark,
      'border-red-400': displayError && isDark,
      'cursor-not-allowed': disabled
    }
  );

  const phoneInputClassName = clsx(
    'flex-1 px-3 py-1 bg-transparent placeholder:text-xs border-none outline-none',
    {
      'text-slate-200 placeholder-slate-400': isDark,
      'text-gray-900 placeholder-gray-400': !isDark,
      'cursor-not-allowed': disabled
    }
  );

  const dropdownClassName = clsx(
    'absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-auto',
    'border rounded-lg shadow-lg',
    {
      'bg-slate-800 border-slate-600': isDark,
      'bg-white border-gray-300': !isDark
    }
  );

  const searchInputClassName = clsx(
    'w-full px-3 py-2 border-b text-sm',
    'focus:outline-none focus:ring-0',
    {
      'bg-slate-800 border-slate-600 text-slate-200 placeholder-slate-400': isDark,
      'bg-white border-gray-200 text-gray-900 placeholder-gray-400': !isDark
    }
  );

  const optionClassName = clsx(
    'flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors',
    {
      'hover:bg-slate-700 text-slate-200': isDark,
      'hover:bg-gray-50 text-gray-900': !isDark
    }
  );

  const errorClassName = clsx(
    'text-sm mt-1',
    {
      'text-red-400': isDark,
      'text-red-600': !isDark
    }
  );

  return (
    <div className={containerClassName}>
      {label && (
        <label className={labelClassName}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <div className={containerClasses}>
          {/* Country Selector */}
          <button
            type="button"
            onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
            disabled={disabled}
            className={countryButtonClassName}
          >
            <span className="text-lg">{selectedCountry.flag}</span>
            <span className="text-sm font-mono">{selectedCountry.dialCode}</span>
            <ChevronDown 
              size={14} 
              className={clsx(
                'transition-transform',
                { 'rotate-180': isDropdownOpen }
              )} 
            />
          </button>

          {/* Phone Number Input */}
          <input
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={phoneInputClassName}
            aria-invalid={displayError ? 'true' : 'false'}
            aria-describedby={displayError ? 'phone-error' : undefined}
          />
        </div>

        {/* Country Dropdown */}
        {isDropdownOpen && (
          <div className={dropdownClassName}>
            {/* Search Input */}
            <input
              type="text"
              placeholder="Search countries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={searchInputClassName}
            />
            
            {/* Country Options */}
            <div className="max-h-48 overflow-y-auto">
              {filteredCountries.map((country) => (
                <div
                  key={country.code}
                  onClick={() => handleCountrySelect(country)}
                  className={optionClassName}
                >
                  <span className="text-lg">{country.flag}</span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{country.name}</div>
                    <div className="text-xs opacity-70">{country.dialCode}</div>
                  </div>
                </div>
              ))}
              {filteredCountries.length === 0 && (
                <div className="px-3 py-2 text-sm opacity-70">
                  No countries found
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {displayError && (
        <div id="phone-error" className={errorClassName}>
          {displayError}
        </div>
      )}

      {/* Click outside handler */}
      {isDropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsDropdownOpen(false)}
        />
      )}
    </div>
  );
};

PhoneNumberField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onCountryChange: PropTypes.func,
  defaultCountry: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  requiredMessage: PropTypes.string,
  invalidFormatMessage: PropTypes.string,
  validateOnBlur: PropTypes.bool,
  validateFormat: PropTypes.bool,
  className: PropTypes.string,
  containerClassName: PropTypes.string
};

export default PhoneNumberField;